const BELT_URL_PARAM_VARIANT = 'variant'

/**
 * Units to add for an addon checkbox. Reads `data-quantity` (set from the
 * addon block's Quantity setting) and falls back to 1 for older markup.
 * @param {HTMLInputElement} checkbox
 */
function addonQuantity(checkbox) {
  const parsed = Number.parseInt(checkbox.dataset.quantity || '1', 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
}

// Bundle parents reject parent_id. Nest keys match hybrid-cart.
function nestBundleItems(items) {
  if (!items.some((item) => item.parent_id != null)) return items
  const nestId = crypto.randomUUID()
  return items.map((item) => {
    if (item.parent_id == null) {
      return { ...item, properties: { ...(item.properties || {}), _HC_NEST_ID: nestId } }
    }
    const { parent_id, ...rest } = item
    return { ...rest, properties: { ...(rest.properties || {}), _HC_PARENT_KEY: nestId } }
  })
}

class BodyBeltProductForm extends HTMLElement {
  connectedCallback() {
    if (!this.initialized) {
      this.initialized = true
      this.isInitialLoad = true
      this.variants = this.readVariants()
      this.applyBeltQueryParamsToForm()
      this.setupAddonPreventUncheck()
      this.setupPriceUpdating()
    }
    this.setupAddToCart()
  }

  readVariants() {
    const variantsData = this.querySelector('#product-variants-data')
    if (!variantsData) return []
    try {
      const variants = JSON.parse(variantsData.textContent || '[]')
      return Array.isArray(variants) ? variants : []
    } catch (error) {
      console.error('[body-belt-product-form] Error parsing product variants:', error)
      return []
    }
  }

  setupAddonPreventUncheck() {
    const interceptUncheckIntent = (event) => {
      if (!(event.target instanceof Element)) return
      if (event.target.closest('button, a[href]')) return
      /** @type {HTMLInputElement | null} */
      let input = null
      if (event.target instanceof HTMLInputElement && event.target.name === 'addon-product') {
        input = event.target
      } else {
        const label = event.target.closest('label')
        const control =
          label && typeof HTMLLabelElement !== 'undefined' && label instanceof HTMLLabelElement ? label.control : null
        if (control instanceof HTMLInputElement && control.name === 'addon-product') input = control
      }
      if (!input || input.type !== 'checkbox') return
      if (!input.hasAttribute('data-prevent-uncheck')) return
      if (!input.checked) return
      event.preventDefault()
    }

    const revertDisallowedUncheck = (event) => {
      const input = event.target
      if (!(input instanceof HTMLInputElement) || input.name !== 'addon-product') return
      if (!input.hasAttribute('data-prevent-uncheck')) return
      if (input.checked) return
      input.checked = true
    }

    this.addEventListener('click', interceptUncheckIntent, true)
    this.addEventListener('change', revertDisallowedUncheck)
  }

  setupAddToCart() {
    this.onDocumentAddToCartClick =
      this.onDocumentAddToCartClick ||
      ((e) => {
        if (!(e.target instanceof Element)) return
        const $button = e.target.closest('[data-add-to-cart]')
        if (!$button || $button.disabled) return
        if (this.hasAttribute('data-force-out-of-stock')) return
        const variant = this.getSelectedVariant()
        if (!variant || variant.available === false) return
        $button.dataset.loading = ''
        this.executeAddToCart(this.buildAddToCartPayload(variant), $button)
      })
    document.addEventListener('click', this.onDocumentAddToCartClick)
  }

  disconnectedCallback() {
    if (this.onDocumentAddToCartClick) {
      document.removeEventListener('click', this.onDocumentAddToCartClick)
    }
  }

  getBeltSelection() {
    const $model = this.querySelector('[data-belt-input="model"]:checked')
    const variantId = Number($model?.dataset.beltVariantId || this.getAttribute('variant-id'))
    return { variantId, modelInput: $model }
  }

  getSelectedVariant() {
    const { variantId } = this.getBeltSelection()
    return (
      this.variants.find((variant) => Number(variant.id) === variantId) ||
      this.variants.find((variant) => variant.available) ||
      this.variants[0] ||
      null
    )
  }

  buildAddToCartPayload(variant) {
    // Desired cart display (top → bottom):
    // main → upgrades → auto-addons/GWP → selected add-ons → warranties.
    // executeAddToCart reverses for Shopify cart/add.js prepend behavior.
    const parentVariantId = Number(variant.id)
    const items = [{ quantity: 1, id: parentVariantId }]

    const upgrades = this.getSelectedUpgrade()
    if (upgrades) {
      upgrades.forEach((upgrade) => items.push({ ...upgrade, parent_id: parentVariantId }))
    }

    this.getAutoAddons().forEach((addon) => items.push({ ...addon, parent_id: parentVariantId }))
    this.getSelectedAddons().forEach((addon) => items.push({ ...addon, parent_id: parentVariantId }))
    this.getSelectedWarranties().forEach((warranty) => items.push({ ...warranty, parent_id: parentVariantId }))

    return { items }
  }

  getSelectedUpgrade() {
    const $upgrade = this.querySelector('[name="product-upgrade"]:checked')
    if (!($upgrade instanceof HTMLInputElement)) return null
    if (!$upgrade.value) return null
    const items = [{ quantity: 1, id: Number($upgrade.value), properties: { _upgrade: true } }]
    const variant2 = $upgrade.dataset.upgradeSecondVariant
    if (variant2) {
      items.push({ quantity: 1, id: Number(variant2), properties: { _upgrade: true } })
    }
    return items
  }

  getAutoAddons() {
    try {
      const autoAddons = JSON.parse(this.getAttribute('auto-addons') || '[]')
      return Array.isArray(autoAddons) ? autoAddons : []
    } catch (error) {
      console.error('[body-belt-product-form] Error parsing autoAddons:', error)
      return []
    }
  }

  /** @param {HTMLInputElement} checkbox */
  getAddonSellingAndCompareAtCents(checkbox) {
    const sell = Number.parseInt(checkbox.dataset.price || '0', 10) || 0
    const compareRaw = checkbox.dataset.compareAtPrice
    let compareAt = sell
    if (compareRaw != null && compareRaw !== '') {
      compareAt = Number.parseInt(String(compareRaw), 10) || sell
    }
    return { sell, compareAt }
  }

  getSelectedWarranties() {
    const warranties = []
    this.querySelectorAll('[name="warranty"]:checked').forEach(($w) => {
      if (!($w instanceof HTMLInputElement) || $w.value === '') return
      // Tag as _addon so hybrid cart keeps warranties below the main product.
      warranties.push({ quantity: 1, id: Number($w.value), properties: { _addon: true } })
      const product2 = $w.dataset.warrantyProduct2 || $w.getAttribute('data-warranty-product-2')
      if (product2) {
        warranties.push({ quantity: 1, id: Number(product2), properties: { _addon: true } })
      }
    })
    return warranties
  }

  getSelectedAddons() {
    return Array.from(this.querySelectorAll('[name="addon-product"]:checked'))
      .filter(($addon) => $addon instanceof HTMLInputElement && $addon.value !== '')
      .map(($addon) => ({
        quantity: addonQuantity($addon),
        id: Number($addon.value),
        properties: { _addon: true },
      }))
  }

  executeAddToCart(addToCartPayload, $addToCartButton) {
    let cartItems = window.igApplyCartNesting([...addToCartPayload.items].reverse())
    if (this.dataset.bundle === 'true') cartItems = nestBundleItems(cartItems)
    const payload = { items: cartItems }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (!response.ok) {
          const error = await response.json()
          return alert(error.description || error.message)
        }
        window.HybridCart.open()
      })
      .catch((response) => {
        console.error('[body-belt-product-form] Error adding to cart:', response)
      })
      .finally(() => {
        delete $addToCartButton.dataset.loading
      })
  }

  setupPriceUpdating() {
    const automaticDiscount = Number(this.getAttribute('automatic-discount')) || 0
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      trailingZeroDisplay: 'stripIfInteger',
    })

    const updatePrice = () => {
      const variant = this.getSelectedVariant()
      if (!variant) return

      this.syncBeltUrlState(variant)
      this.updateAddToCart(variant)

      const variantPrice = Number(variant.price) || 0
      const variantCompareAt = Number(variant.compare_at_price || variant.price) || 0
      const beltSellCents = Math.max(variantPrice - automaticDiscount, 0)
      const beltCompareAtCents = variantCompareAt > variantPrice ? variantCompareAt : variantPrice

      // Addon and product upgrade selections intentionally do NOT affect the
      // page-wide displayed price/savings/Affirm — they only impact what is
      // added to the cart on submit. Their own "+$X" labels are rendered by the
      // liquid template.
      const totalSellCents = beltSellCents
      const totalCompareAtCents = beltCompareAtCents

      this.updatePriceText(totalSellCents, totalCompareAtCents, formatter)
      this.updateAffirm(totalSellCents)
      this.syncMedia(variant)
    }

    this.querySelectorAll('[data-belt-input="model"], [name="addon-product"]').forEach(($selector) => {
      $selector.addEventListener('change', updatePrice)
      $selector.addEventListener('click', updatePrice)
    })

    updatePrice()
  }

  updateAddToCart(variant) {
    const forceOOS = this.hasAttribute('data-force-out-of-stock')
    const unavailable = forceOOS || variant.available === false
    this.querySelectorAll('[data-add-to-cart]').forEach((button) => {
      const text = button.querySelector('[data-text]')
      button.toggleAttribute('disabled', unavailable)
      button.classList.toggle('pointer-events-none', unavailable)
      button.classList.toggle('cursor-not-allowed', unavailable)
      if (text) text.textContent = unavailable ? 'SOLD OUT' : 'Add to Cart'
    })
  }

  updatePriceText(totalSellCents, totalCompareAtCents, formatter) {
    const formattedPrice = formatter.format(totalSellCents / 100)
    document.querySelectorAll('[data-main-product-price]').forEach(($price) => {
      $price.textContent = formattedPrice
    })

    const hasCompareAt = totalCompareAtCents > totalSellCents
    const formattedCompareAtPrice = formatter.format(totalCompareAtCents / 100)
    document.querySelectorAll('[data-main-product-compare-at-price]').forEach(($compareAtPrice) => {
      $compareAtPrice.textContent = `(Total Value ${formattedCompareAtPrice})`
    })

    document.querySelectorAll('[data-apex-compare-line]').forEach((row) => {
      if (row instanceof HTMLElement) row.classList.toggle('hidden', !hasCompareAt)
    })

    document.querySelectorAll('[data-apex-save-pill]').forEach((el) => {
      if (el instanceof HTMLElement) el.classList.toggle('hidden', !hasCompareAt)
    })

    document.querySelectorAll('[data-promo-text]').forEach(($promoText) => {
      if ($promoText.dataset.autoDiscountPromo === 'true') return
      const originalText = $promoText.dataset.promoOriginalText || $promoText.textContent
      const formattedSavings = formatter.format(Math.max(0, totalCompareAtCents - totalSellCents) / 100)
      $promoText.textContent = originalText.replace(/\$[\d,]+(?:\.\d{2})?/, formattedSavings)
    })
  }

  updateAffirm(totalPrice) {
    const affirmEl = this.querySelector('[data-body-affirm-ala]')
    if (!affirmEl) return
    affirmEl.setAttribute('data-amount', String(Math.round(totalPrice)))
    if (typeof window.affirm === 'object' && window.affirm.ui && typeof window.affirm.ui.refresh === 'function') {
      try {
        window.affirm.ui.refresh()
      } catch (_) {}
    }
  }

  applyBeltQueryParamsToForm() {
    if (typeof window === 'undefined' || !window.location?.search) return
    try {
      const variantParam = new URLSearchParams(window.location.search).get(BELT_URL_PARAM_VARIANT)
      if (!variantParam) return
      for (const el of this.querySelectorAll('[data-belt-input="model"]')) {
        if (el instanceof HTMLInputElement && el.dataset.beltVariantId && String(el.dataset.beltVariantId) === variantParam) {
          el.checked = true
          break
        }
      }
    } catch (_) {}
  }

  syncBeltUrlState(variant) {
    const vid = Number(variant.id)
    if (!Number.isFinite(vid) || vid <= 0) return

    const $lineItemId = this.querySelector('input[name="id"]')
    if ($lineItemId instanceof HTMLInputElement) $lineItemId.value = String(vid)
    this.setAttribute('variant-id', String(vid))

    if (typeof window === 'undefined' || !window.history?.replaceState) return
    try {
      const url = new URL(window.location.href)
      if (url.searchParams.get(BELT_URL_PARAM_VARIANT) === String(vid)) return
      url.searchParams.set(BELT_URL_PARAM_VARIANT, String(vid))
      window.history.replaceState({}, '', url.toString())
    } catch (_) {}
  }

  getVariantMediaIds(variant) {
    return [variant.featured_media?.id, variant.featured_image?.id].filter(Boolean).map(String)
  }

  findSlideByMediaIds(slides, mediaIds) {
    const desired = new Set(mediaIds)
    return slides.find(
      (slide) =>
        desired.has(slide.getAttribute?.('data-media-id')) || desired.has(slide.getAttribute?.('data-image-id'))
    )
  }

  rememberOriginalMediaOrder(mediaRoot) {
    if (mediaRoot._beltProductOriginalMainSlides) return
    const mainList = mediaRoot.querySelector('[data-product-media-v2-main] .splide__list')
    const thumbsList = mediaRoot.querySelector('[data-product-media-v2-thumbs-list]')
    mediaRoot._beltProductOriginalMainSlides = mainList ? Array.from(mainList.children) : []
    mediaRoot._beltProductOriginalThumbSlides = thumbsList ? Array.from(thumbsList.children) : []
  }

  reorderMediaList(list, originalSlides, selectedSlide, placement) {
    if (!list) return []
    const seeItSlide =
      placement === 'after-video'
        ? originalSlides.find((slide) => slide.hasAttribute('data-see-it-in-action-main-slide'))
        : null
    const leadingSlides = [seeItSlide, selectedSlide].filter(Boolean)
    if (!leadingSlides.length) return []

    const orderedSlides = [...leadingSlides, ...originalSlides.filter((slide) => !leadingSlides.includes(slide))]
    list.replaceChildren(...orderedSlides)
    return orderedSlides
  }

  reorderThumbList(mediaRoot, mainOrderedSlides) {
    const thumbsList = mediaRoot.querySelector('[data-product-media-v2-thumbs-list]')
    const originalThumbSlides = mediaRoot._beltProductOriginalThumbSlides || []
    if (!thumbsList || !originalThumbSlides.length || !mainOrderedSlides.length) return

    const orderedThumbs = mainOrderedSlides
      .map((slide) => slide.getAttribute('data-body-slide-index'))
      .map((slideIndex) => originalThumbSlides.find((thumb) => thumb.getAttribute('data-body-slide-index') === slideIndex))
      .filter(Boolean)

    orderedThumbs.forEach((thumb, index) => {
      const button = thumb.querySelector('[data-product-media-v2-thumb]')
      if (button) button.dataset.slideIndex = String(index)
    })
    if (orderedThumbs.length === mainOrderedSlides.length) {
      thumbsList.replaceChildren(...orderedThumbs)
    }
  }

  syncMedia(variant, { attempt = 0 } = {}) {
    const sectionId = this.dataset.sectionId
    if (!sectionId) return

    const placement = this.isInitialLoad ? 'after-video' : 'first'
    this.isInitialLoad = false

    const mediaRoot = document.querySelector(`[data-product-media-v2="${CSS.escape(sectionId)}"]`)
    const splide = mediaRoot?._bodyProductMediaSplide
    if (!mediaRoot || !splide) {
      if (attempt < 60) {
        requestAnimationFrame(() => this.syncMedia(variant, { attempt: attempt + 1 }))
      }
      return
    }

    this.rememberOriginalMediaOrder(mediaRoot)
    const mainList = mediaRoot.querySelector('[data-product-media-v2-main] .splide__list')
    const originalMainSlides = mediaRoot._beltProductOriginalMainSlides || []

    const mediaIds = this.getVariantMediaIds(variant)
    const selectedSlide = mediaIds.length ? this.findSlideByMediaIds(originalMainSlides, mediaIds) : null

    const orderedSlides = this.reorderMediaList(mainList, originalMainSlides, selectedSlide, placement)
    if (orderedSlides.length) {
      this.reorderThumbList(mediaRoot, orderedSlides)
      try {
        splide.refresh()
        mediaRoot._bodyProductThumbSplide?.refresh?.()
        mediaRoot._applyProductGalleryRoles?.()
        splide.go(0)
      } catch (_) {}
    }
  }
}

if (!customElements.get('body-belt-product-form')) {
  customElements.define('body-belt-product-form', BodyBeltProductForm)
}

/**
 * Shared open/close/focus-trap/Escape handling for belt upgrade info modals (v1 + v2).
 * @param {{
 *   rootId: string,
 *   openSelector: string,
 *   cardSelector?: string,
 *   closeSelector?: string,
 * }} options
 */
function initBeltUpgradeModal({
  rootId,
  openSelector,
  cardSelector = '[data-belt-upgrade-modal-card]',
  closeSelector = '[data-belt-upgrade-modal-close], [data-belt-upgrade-modal-bg]',
}) {
  const root = document.getElementById(rootId)
  if (!root) return

  const card = root.querySelector(cardSelector)
  const openButtons = document.querySelectorAll(openSelector)
  const closeEls = root.querySelectorAll(closeSelector)
  let lastFocused = null

  const open = () => {
    lastFocused = document.activeElement
    root.classList.add('!visible', '!opacity-100')
    if (card) card.classList.add('!translate-y-0')
    document.body.style.overflow = 'hidden'

    const focusTarget = card?.querySelector(closeSelector) || card
    if (typeof trapFocus === 'function' && card && focusTarget) {
      setTimeout(() => trapFocus(card, focusTarget), 50)
    } else if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus()
    }
  }

  const close = () => {
    root.classList.remove('!visible', '!opacity-100')
    if (card) card.classList.remove('!translate-y-0')
    document.body.style.overflow = ''
    if (typeof removeTrapFocus === 'function') removeTrapFocus()
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus()
  }

  openButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault()
      open()
    })
  })

  closeEls.forEach((el) => {
    el.addEventListener('click', close)
  })

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root.classList.contains('!visible')) close()
  })
}

window.initBeltUpgradeModal = initBeltUpgradeModal
